<template>
  <v-card v-if="ticket" id="ticket-detail">
    <v-card-title :class="headerClass">
      <Icon
      v-if="ticket.status !== undefined"
      dataTestId="ticket-detail-status-icon"
      :icon="ticketStatusIcon(ticket).icon"
      iconColor="tertiary"
      :small="false"
      :tooltipText="ticketStatusIcon(ticket).tooltipText"/>
      <span class="headline mr-2" v-if="ticket.status !== undefined">{{title}}</span>
      <Icon
      v-if="ticket.proximityTriggered"
      icon="mdi-alert-circle-outline"
      dataTestId="ticket-detail-prox-icon"
      iconColor="tertiary"
      :small="false"
      :tooltipText="$t('loadCreatedOffsite')"/>
      <Icon
      v-if="missingLogCounts(ticket)"
      icon="mdi-numeric-off"
      dataTestId="ticket-detail-missing-log-counts"
      iconColor="tertiary"
      :small="false"
      :tooltipText="$t('missingLogCounts')"
      />
      <Icon
      iconColor="tertiary"
      icon="mdi-file"
      dataTestId="ticket-detail-draft-icon"
      v-if="ticket.isOnDraftContract"
      :tooltipText="$t('draftTicket')"
      />
      <Icon
      v-if="ticket.consumed"
      iconColor="tertiary"
      icon="mdi-saw-blade"
      dataTestId="ticket-detail-consume-icon"
      :tooltipText="$t('consumed')"
      :small="false"
      />
      <v-spacer/>
      <BaseDialogActions
      @close="$emit('close')"
      hideRefresh>
        <template v-slot:left>
          <v-menu
            bottom
            left>
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                medium
                class="mr-2"
                v-bind="attrs"
                color="tertiary"
                v-on="on">mdi-dots-vertical</v-icon>
            </template>

            <v-list id="ticket-detail-header-actions">
              <v-list-item
                v-for="(item, i) in headerActions"
                :key="i"
                :disabled="item.disabled"
                @click="item.action"
                :id="item.id"
              >
                <v-avatar left>
                  <v-icon style="width: 25px; height: 25px;" color="black" :disabled="item.disabled">{{item.icon}}</v-icon>
                </v-avatar>
                <v-list-item-title class="subtitle-1">{{ item.tooltipText }}</v-list-item-title>
              </v-list-item>
              <TicketScaleDataImporter
                :propTicket="ticket"
                mode="volume"
                @refresh="refreshTicket"
              />
            </v-list>
          </v-menu>
        </template>
      </BaseDialogActions>
    </v-card-title>

    <v-card-text class="pt-6">
      <v-tabs v-model="ticketDetailTab" class="card-tabs" v-if="showScaleData">
        <v-tab>
          <v-icon class="mr-1">mdi-clipboard-outline</v-icon>
          <span> {{ $t('general') }} </span>
        </v-tab>

        <v-tab>
          <v-icon class="mr-1">mdi-ruler-square</v-icon>
          <span> {{ $t('scaling') }} </span>
        </v-tab>
      </v-tabs>

      <v-window v-model="ticketDetailTab">
        <v-window-item class="mx-n3 mt-n1">
          <v-row no-gutters>
            <v-col cols="12">
              <TicketInformation
              :ticket="mappedTicketWithCorrectedDriver"
              :corrections="ticketCorrections"
              :shouldFetchImages="ticket.hasImages"
              v-if="ticket"
              v-on:logCounts="logCountsDialog"
              :styleForTabs="showScaleData"/>
            </v-col>

            <v-col cols="12" class="mt-n4 mb-5">
              <CorrectionTable
              :ticketId="ticketId"
              :ticket="ticket"
              :ticketCorrections="ticketCorrections"
              @refresh="refreshTicket"
              v-if="isExported && ticket && ticketCorrections?.length > 0"
              v-bind="{ contractMode }"/>
            </v-col>

            <v-col cols="12">
              <TicketFinancials
              v-if="ticket"
              :ticket="ticket"
              :ticketId="ticketId"
              :refreshToggle="correctionRefreshToggle"
              :contractMode="contractMode.value"
              @financials-fetched="handleFinancialsFetched"
            />
            </v-col>
          </v-row>
        </v-window-item>

        <v-window-item>
          <v-card outlined>
            <v-card-text>
              <v-row>
                <v-col cols="12">
                  <TicketScaleSummary
                  :ticket="ticket"
                  :financials="financialData"
                  @re-expand-ticket="expandTicket"
                  />
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-window-item>
      </v-window>

      <Dialog :stateId="dialogId" @close="resetDialogs" height="80vh" fullscreenBreakPoint="smAndDown">
        <InteractiveMap
        v-if="ticketMap"
        :ticket="ticket"
        @map-closed="resetDialogs"
        :height="$vuetify.breakpoint.smAndDown ? '100%' : '80vh'">
        </InteractiveMap>

        <TicketCorrectionForm
        :ticket="ticket"
        :defects="allTicketDefects"
        @close="resetDialogs"
        v-if="ticketCorrectionDialog"
        :contractMode="contractMode"
        @correction-created="correctionCreated"/>

        <LogCountChart v-if="logCounts"
        :ticketId="ticket.ticketId"
        v-on:close="resetDialogs"/>

        <ModificationHistory
        v-if="ticket && modificationHistoryDialog"
        entityType="ticket"
        :entityId="ticket?.ticketId"
        :entityName="`${$t('ticket')} #${ticket.ticketNumber}`"
        :headerClass="headerClass"
        />
      </Dialog>
    </v-card-text>
  </v-card>
</template>

<script>
import { mapGetters, mapMutations, mapActions } from 'vuex'
import { getTicketPDF } from '@/utils/pdf-formatters/Ticket.js'
import { exportTicketScaleSheetCSV } from '@/utils/csv/CSVExports'
import { TicketStatus, TicketStatusIcon, UserClaims, ScalingStatus, ContractType, ContractMode } from '@/utils/Enumerations.js'
import { LocalStorageKeys } from '@/utils/LocalStorageActor'
import RouterJump from '@/model/RouterJump.js'
import { uniqueDialogId, colorClassForContractType, colorClassForContractMode } from '@/utils/componentHelpers'
import { userAssignedClaim } from '@/utils/ClaimUtility.js'
import { DialogWidth } from '@/utils/Dialog.js'
import { printerWithScaleRuler } from '@/images/AngelSvgLibrary'
export default {
  name: 'TicketDetails',

  props: {
    ticketId: {
      type: Number,
      required: true
    },
    showCrud: {
      type: Boolean,
      default: true
    },
    initialTab: {
      type: String,
      default: 'general'
    }
  },

  components: {
    CorrectionTable: () => import('@/components/ticket/corrections/CorrectionTable.vue'),
    ModificationHistory: () => import('@/components/admin/ModificationHistory.vue'),
    Icon: () => import('@/components/helper/Icon.vue'),
    InteractiveMap: () => import('@/components/maps/InteractiveMap.vue'),
    TicketFinancials: () => import('@/components/ticket/ticket-details/TicketFinancials.vue'),
    TicketInformation: () => import('@/components/ticket/ticket-details/TicketInformation'),
    TicketCorrectionForm: () => import('@/components/ticket/corrections/form/CorrectionForm.vue'),
    BaseDialogActions: () => import('@/components/core/BaseDialogActions.vue'),
    LogCountChart: () => import('@/components/ticket/ticket-details/LogCountChart.vue'),
    TicketScaleSummary: () => import('@/components/ticket/ticket-details/scale-data/TicketScaleSummary.vue'),
    TicketScaleDataImporter: () => import('@/components/ticket/ticket-details/scale-data/TicketScaleDataImporter.vue')
  },

  data: () => ({
    UserClaims,
    ticket: undefined,
    currentTab: 0,
    ticketMap: false,
    focusedCorrection: null,
    ticketCorrections: [],
    logCounts: false,
    shouldRefreshCorrections: false,
    correctionRefreshToggle: false,
    ticketCorrectionDialog: false,
    modificationHistoryDialog: false,
    dialogId: uniqueDialogId('ticket-details'),
    correctedDriver: undefined,
    ticketDetailTab: 0,
    financialData: null,
    printerWithScaleRuler
  }),

  created () {
    this.refreshTicket(true)
  },

  computed: {
    ...mapGetters('user', ['companyInfo']),
    ...mapGetters('locations', ['allLocations']),
    ...mapGetters('defects', ['allTicketDefects']),
    title () {
      return `Ticket #${this.ticket.ticketNumber}`
    },

    contractMode () {
      switch (this.ticket?.contractType) {
        case ContractType.ByproductSale.value:
        case ContractType.ByproductPurchase.value:
        case ContractType.ByproductHauling.value:
          return ContractMode.Byproducts
        case ContractType.Transfer.value:
          return ContractMode.Transfers
        case ContractType.LogYardSale.value:
          return ContractMode.LogYardSale
        default:
          return ContractMode.Logs
      }
    },

    headerClass () {
      return this.ticket.contractType
        ? colorClassForContractType(this.ticket.contractType)
        : colorClassForContractMode(this.contractMode.value)
    },

    isExported () {
      return this.ticket.status === TicketStatus.Exported.value
    },

    isReadOnly () {
      return (this.ticket.status === TicketStatus.Posted.value ||
        this.ticket.status === TicketStatus.Recalculating.value ||
        this.isExported)
    },

    headerActions () {
      const actions = [
        (this.isExported) ? {
          icon: 'mdi-auto-fix',
          disabled: this.ticketCorrections.length > 0 || !userAssignedClaim(UserClaims.TicketManager),
          tooltipText: this.$t('correctTicket'),
          action: this.openTicketCorrectionForm,
          id: 'correct'
        } : undefined,
        (this.showCrud) ? {
          icon: 'mdi-pencil',
          disabled: this.isReadOnly || !userAssignedClaim(UserClaims.TicketManager),
          tooltipText: this.$t('editTicket'),
          action: this.editTicket,
          id: 'edit'
        } : undefined,
        (this.ticket.scalingStatus === ScalingStatus.Ready.value) ? {
          icon: 'mdi-alpha-e-circle-outline',
          tooltipText: this.$t('expandTicket'),
          action: this.expandTicket,
          disabled: (this.ticket.status !== TicketStatus.WeighedOut.value && this.ticket.scalingStatus !== ScalingStatus.Ready.value) || !userAssignedClaim(UserClaims.TicketManager),
          id: 'expand-ticket'
        } : undefined,
        {
          icon: 'mdi-history',
          tooltipText: this.$t('viewModifications'),
          action: this.openModificationHistoryDialog,
          id: 'modifications'
        },
        {
          icon: 'mdi-map',
          tooltipText: this.$t('viewOnMap'),
          action: this.openMap,
          disabled: this.ticket.latitude === 0 && this.ticket.longitude === 0,
          id: 'map'
        },
        {
          icon: 'mdi-printer',
          tooltipText: this.$t('printTicket'),
          action: this.printTicket,
          id: 'print-tickets'
        }
      ].filter(a => a)

      return actions
    },

    mappedTicketWithCorrectedDriver () {
      return {
        ...this.ticket,
        correctedDriver: this.correctedDriver
      }
    },

    showScaleData () {
      return (this.ticket?.scalingStatus === ScalingStatus.Complete.value) && this.financialData !== null
    }
  },

  watch: {
    showScaleData (value) {
      if (value && this.initialTab === 'scaling') {
        this.ticketDetailTab = 1
      }
    }
  },

  methods: {
    ...mapMutations('global', ['setRouterJump']),
    ...mapActions('ticket', ['fetchTicketById', 'getCorrectionsForTicket', 'patchTicket']),
    ...mapActions('locations', ['fetchLocationWithId']),
    ...mapActions('dialog', ['openOrUpdateDialog', 'closeDialogsAtOrAbove']),
    ...mapActions('account', ['fetchAccountById']),
    ...mapMutations('snackbar', ['setSnack']),
    ...mapActions('account', ['fetchTicketBooks']),
    ...mapActions('species', ['fetchSpecies']),
    ...mapActions('grade', ['fetchGrades']),
    ticketStatus: (x) => TicketStatus.fromInt(x),
    userAssignedClaim,
    ticketStatusIcon ({ status, transitStatus, vehicleType, scalingStatus }) {
      return TicketStatusIcon(status, transitStatus, vehicleType, scalingStatus)
    },

    missingLogCounts (ticket) {
      if (ticket.destinationAccountId === undefined || this.contractMode?.value !== 0) return false
      const selectedLocation = this.allLocations.find(location => location.accountId === ticket.destinationAccountId)
      return selectedLocation?.collectLogCounts && !ticket.hasLogCounts
    },

    async refreshTicket (initialRefresh) {
      const response = await this.fetchTicketById(this.ticketId)
      this.ticket = response
      if (this.isExported) {
        this.refreshCorrections(initialRefresh)
      }
    },

    editTicket () {
      const currentPath = this.$router.app._route.path
      if (currentPath === '/tickets' || currentPath === '/tracts') {
        this.$emit('edit-ticket', {
          actionType: 'open-form',
          ticket: this.ticket
        })
      } else {
        this.jumpToTickets()
      }
    },

    async refreshCorrections (initialRefresh) {
      if (this.ticket?.correctionType === null) {
        this.ticketCorrections = []
        this.correctedDriver = undefined
      }

      this.ticketCorrections = await this.getCorrectionsForTicket(this.ticketId)
      if (!initialRefresh) {
        this.correctionRefreshToggle = !this.correctionRefreshToggle
      }
      if (this.ticketCorrections.length === 0) return
      const driverAccountId = this.ticketCorrections[this.ticketCorrections.length - 1].correction.driverAccountId
      if (!driverAccountId) return
      this.correctedDriver = (await this.fetchAccountById(driverAccountId)).name
    },

    logCountsDialog () {
      this.logCounts = true
      this.openOrUpdateDialog({ id: this.dialogId, width: '40vw' })
    },

    jumpToTickets () {
      if (!this.ticket.tract) {
        localStorage.setItem(LocalStorageKeys.BP_TICKETS_VIEW, true)
      } else {
        localStorage.setItem(LocalStorageKeys.BP_TICKETS_VIEW, false)
      }
      const jump = new RouterJump(
        'TicketDetail',
        'tickets',
        this.ticket
      )
      this.setRouterJump(jump)
      this.$router.push('tickets')
    },

    printTicket () {
      getTicketPDF(this.ticket, this.companyInfo)
    },

    async printScaleSheet () {
      await exportTicketScaleSheetCSV(this.ticket)
    },

    openMap () {
      this.ticketMap = true
      this.openOrUpdateDialog({ id: this.dialogId, width: DialogWidth.Standard })
    },

    openTicketCorrectionForm () {
      this.ticketCorrectionDialog = true
      this.openOrUpdateDialog({ id: this.dialogId, width: DialogWidth.SimpleWide })
    },

    openModificationHistoryDialog () {
      this.modificationHistoryDialog = true
      this.openOrUpdateDialog({ id: this.dialogId, width: DialogWidth.Standard })
    },

    resetDialogs () {
      this.closeDialogsAtOrAbove(this.dialogId)
      this.modificationHistoryDialog = false
      this.ticketCorrectionDialog = false
      this.logCounts = false
      this.ticketMap = false
    },

    correctionCreated ({ ticketCorrectionId, ticketId }) {
      this.focusedCorrection = {
        correctionId: ticketCorrectionId,
        ticketId: ticketId
      }
      this.resetDialogs()
      this.refreshTicket()
    },

    handleFinancialsFetched (financials) {
      this.financialData = financials
    },

    async expandTicket () {
      await this.patchTicket({
        ticketId: this.ticket.ticketId,
        modifiedAt: this.ticket.modifiedAt,
        patchObject: [{
          op: 'add',
          path: '/scalingStatus',
          value: ScalingStatus.Expansion.value
        }]
      })
      this.$emit('close')
    }
  }
}
</script>
